import {
    SHOW_CHEMICAL_INFO_MODAL,
    HIDE_CHEMICAL_INFO_MODAL,
    SET_CHEMICAL_INFO_MODAL_ACTIVE_TAB,
    REQUEST_CHEMICAL_INFO_MODAL_ANALYSIS,
    CHEMICAL_INFO_MODAL_SEARCH_COMPOUNDS,
    CHEMICAL_INFO_MODAL_LOAD_COMPOUNDS,
    CHEMICAL_INFO_MODAL_ESTIMATE_COMPOUND,
} from "../actions/chemicalInfoModal";
import type {
    CompoundEstimationTaskResponseMessage,
    PocketbookFatModel,
    PocketbookSearchModel,
} from "../../data/models/schemas";
import type { IChemicalModel } from "../../data/ChemicalModel";
import type { IChemical, IReactionChemical } from "../actions/library";
import { getCompounds } from "./pocketbook";

export type IChemicalInfoModalProgress = Pick<
    CompoundEstimationTaskResponseMessage,
    "phase" | "completedTasks" | "totalTasks" | "percentComplete"
>;

export interface IChemicalInfoModalState {
    show: boolean;
    chemical: IChemical | IReactionChemical | null;
    activeTab: "basic-info" | "analysis";
    compounds: IChemicalModel[];
    searchResults: PocketbookSearchModel[];
    loadingState: "idle" | "loading" | "success" | "error";
    progress: IChemicalInfoModalProgress | null;
    activeEstimationRequestId: string | null;
    failureSource: "sync" | "estimation" | null;
}

const initialState: IChemicalInfoModalState = {
    show: false,
    chemical: null,
    activeTab: "basic-info",
    compounds: [],
    searchResults: [],
    loadingState: "idle",
    progress: null,
    activeEstimationRequestId: null,
    failureSource: null,
};

type ChemicalInfoModalReducerPayload = {
    chemical?: IChemical | IReactionChemical;
    activeTab?: "basic-info" | "analysis";
    models?: Array<PocketbookSearchModel | PocketbookFatModel>;
    phase?: CompoundEstimationTaskResponseMessage["phase"];
    completedTasks?: CompoundEstimationTaskResponseMessage["completedTasks"];
    totalTasks?: CompoundEstimationTaskResponseMessage["totalTasks"];
    percentComplete?: CompoundEstimationTaskResponseMessage["percentComplete"];
    requestId?: string;
};

const getProgress = (
    payload?: ChemicalInfoModalReducerPayload
): IChemicalInfoModalProgress | null => {
    if (
        payload?.phase === undefined &&
        payload?.completedTasks === undefined &&
        payload?.totalTasks === undefined &&
        payload?.percentComplete === undefined
    ) {
        return null;
    }

    return {
        phase: payload.phase,
        completedTasks: payload.completedTasks,
        totalTasks: payload.totalTasks,
        percentComplete: payload.percentComplete,
    };
};

export const chemicalInfoModalReducer = (
    state: IChemicalInfoModalState = initialState,
    action: {
        type: string;
        payload?: ChemicalInfoModalReducerPayload;
    }
): IChemicalInfoModalState => {
    switch (action.type) {
        case SHOW_CHEMICAL_INFO_MODAL:
            return {
                ...state,
                show: true,
                chemical: action.payload?.chemical ?? null,
                activeTab: "basic-info",
                compounds: [],
                searchResults: [],
                loadingState: "idle",
                progress: null,
                activeEstimationRequestId: null,
                failureSource: null,
            };

        case HIDE_CHEMICAL_INFO_MODAL:
            return {
                ...initialState,
            };

        case SET_CHEMICAL_INFO_MODAL_ACTIVE_TAB:
            return {
                ...state,
                activeTab: action.payload?.activeTab ?? state.activeTab,
            };

        case REQUEST_CHEMICAL_INFO_MODAL_ANALYSIS:
            return {
                ...state,
                loadingState: "loading",
                activeTab: "analysis",
                progress: null,
                activeEstimationRequestId: null,
                failureSource: null,
            };

        case `${CHEMICAL_INFO_MODAL_SEARCH_COMPOUNDS}_REQUEST`:
            return {
                ...state,
                loadingState: "loading",
                searchResults: [],
                progress: null,
                activeEstimationRequestId: null,
                failureSource: null,
            };

        case `${CHEMICAL_INFO_MODAL_SEARCH_COMPOUNDS}_SUCCESS`: {
            const searchResults = (action.payload?.models ||
                []) as PocketbookSearchModel[];
            const hasLoadableResult = searchResults.some(
                (result) => result.id !== undefined
            );
            return {
                ...state,
                searchResults,
                loadingState: hasLoadableResult ? "loading" : "success",
                failureSource: null,
            };
        }

        case `${CHEMICAL_INFO_MODAL_SEARCH_COMPOUNDS}_ERROR`:
            return {
                ...state,
                loadingState: "error",
                searchResults: [],
                progress: null,
                failureSource: "sync",
            };

        case `${CHEMICAL_INFO_MODAL_LOAD_COMPOUNDS}_REQUEST`:
            return {
                ...state,
                loadingState: "loading",
                progress: null,
                activeEstimationRequestId: null,
                failureSource: null,
            };

        case `${CHEMICAL_INFO_MODAL_LOAD_COMPOUNDS}_SUCCESS`:
            return {
                ...state,
                compounds: (action.payload?.models || []) as IChemicalModel[],
                loadingState: "success",
                progress: null,
                failureSource: null,
            };

        case `${CHEMICAL_INFO_MODAL_LOAD_COMPOUNDS}_ERROR`:
            return {
                ...state,
                loadingState: "error",
                compounds: [],
                progress: null,
                failureSource: "sync",
            };

        case `${CHEMICAL_INFO_MODAL_ESTIMATE_COMPOUND}_REQUEST`:
            return {
                ...state,
                activeTab: "analysis",
                activeEstimationRequestId: action.payload?.requestId ?? null,
                loadingState: "loading",
                progress: null,
                failureSource: null,
            };

        case `${CHEMICAL_INFO_MODAL_ESTIMATE_COMPOUND}_UPDATE`: {
            if (action.payload?.requestId !== state.activeEstimationRequestId) {
                return state;
            }
            const updatedCompounds = action.payload?.models
                ? getCompounds(
                      state.compounds,
                      action.payload.models as IChemicalModel[],
                      {
                          clearCompounds: state.compounds.length === 0,
                      }
                  )
                : state.compounds;
            return {
                ...state,
                compounds: updatedCompounds,
                loadingState: "loading",
                progress: getProgress(action.payload),
                failureSource: null,
            };
        }

        case `${CHEMICAL_INFO_MODAL_ESTIMATE_COMPOUND}_SUCCESS`: {
            if (action.payload?.requestId !== state.activeEstimationRequestId) {
                return state;
            }
            const completedCompounds = action.payload?.models
                ? getCompounds(
                      state.compounds,
                      action.payload.models as IChemicalModel[],
                      {
                          clearCompounds: state.compounds.length === 0,
                      }
                  )
                : state.compounds;
            return {
                ...state,
                compounds: completedCompounds,
                loadingState: "success",
                progress: null,
                activeEstimationRequestId: null,
                failureSource: null,
            };
        }

        case `${CHEMICAL_INFO_MODAL_ESTIMATE_COMPOUND}_ERROR`: {
            if (action.payload?.requestId !== state.activeEstimationRequestId) {
                return state;
            }
            const partialCompounds = action.payload?.models
                ? getCompounds(
                      state.compounds,
                      action.payload.models as IChemicalModel[]
                  )
                : state.compounds;
            return {
                ...state,
                compounds: partialCompounds,
                loadingState: "error",
                progress: null,
                activeEstimationRequestId: null,
                failureSource: "estimation",
            };
        }

        default:
            return state;
    }
};

export default chemicalInfoModalReducer;
